import { memo, FC, useMemo, useCallback } from 'react'

import {
  TCFPurpose,
  TCFUserDecisionOnVendor,
  TCFUserDecisions,
} from '@usercentrics/cmp-browser-sdk'
import { scroller } from 'react-scroll'

import { Toggle } from '@/components/Toggle/Toggle'
import { orientation } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { PrivacyTcfSectionProps } from '@/types/componentsPropsTypes'

import {
  Section,
  SectionContent,
  SectionDescription,
  SectionTitle,
  SectionTitleWrapper,
  SectionTogglesWrapper,
  ToggleContainer,
  ToggleDescription,
} from './styles'

const PrivacyTcfSectionRaw: FC<PrivacyTcfSectionProps> = ({
  data,
  tabTitle,
  tcfSettingsType,
  tcfUserDecision,
  setTcfUserDecisions,
}) => {
  const { t } = useCommonTranslation()
  const focusId = `Toggle_section_${tabTitle}_${tcfSettingsType}_${data.id}`
  const mainSectionFocusId = `MainSection_${tabTitle}_${tcfSettingsType}_${data.id}`
  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId: mainSectionFocusId,
  })

  const isConsentChecked = useMemo(() => {
    return Boolean(tcfUserDecision?.consent)
  }, [tcfUserDecision])

  const isLegitimateInterestConsentChecked = useMemo(() => {
    return Boolean(tcfUserDecision?.legitimateInterestConsent)
  }, [tcfUserDecision])

  const onToggleHandler = useCallback(
    (id: string) => {
      const isLegitimateInterest = id.includes('legitimateInterest')
      setTcfUserDecisions?.((prev) => {
        const decisions = prev[
          tcfSettingsType as keyof TCFUserDecisions
        ] as TCFUserDecisionOnVendor[]
        const updatedDecisions = decisions?.map((decision) =>
          decision.id === data.id
            ? {
                ...decision,
                ...(!isLegitimateInterest &&
                  decision.consent !== undefined && {
                    consent: !isConsentChecked,
                  }),
                ...(isLegitimateInterest &&
                  decision.legitimateInterestConsent !== undefined && {
                    legitimateInterestConsent:
                      !isLegitimateInterestConsentChecked,
                  }),
              }
            : decision,
        )
        return { ...prev, [tcfSettingsType]: updatedDecisions }
      })
    },
    [
      setTcfUserDecisions,
      isConsentChecked,
      isLegitimateInterestConsentChecked,
      data.id,
    ],
  )

  if (!data) {
    return null
  }

  const scrollHandler = () => {
    scroller.scrollTo(focusId, scrollOptions.UsercentricsScroll)
  }

  return (
    <Section
      orientation={orientation.VERTICAL}
      focusId={focusId}
      onFocused={scrollHandler}
    >
      <SectionContent
        focusId={mainSectionFocusId}
        orientation={orientation.HORIZONTAL}
        defaultFocusChild={defaultFocusChild}
        onMove={onMove}
      >
        <SectionTitleWrapper>
          <SectionTitle>{data.name}</SectionTitle>
          <SectionDescription>
            {(data as TCFPurpose).description}
          </SectionDescription>
        </SectionTitleWrapper>
        <SectionTogglesWrapper orientation={orientation.VERTICAL}>
          {data.showConsentToggle && (
            <ToggleContainer>
              <ToggleDescription>{t('consent')}</ToggleDescription>
              <Toggle
                checked={isConsentChecked}
                onChange={onToggleHandler}
                id={`${tcfSettingsType}_consent_${data.id}`}
                focusId={`${tcfSettingsType}_consent_${data.id}`}
              />
            </ToggleContainer>
          )}
          {(data.showLegitimateInterestToggle ||
            data.showLegitimateInterestConsentToggle) && (
            <ToggleContainer>
              <ToggleDescription>{t('legitimateInterest')}</ToggleDescription>
              <Toggle
                checked={isLegitimateInterestConsentChecked}
                onChange={onToggleHandler}
                id={`${tcfSettingsType}_legitimateInterest_${data.id}`}
                focusId={`${tcfSettingsType}_legitimateInterest_${data.id}`}
              />
            </ToggleContainer>
          )}
        </SectionTogglesWrapper>
      </SectionContent>
    </Section>
  )
}

export const PrivacyTcfSection = memo(PrivacyTcfSectionRaw)
